import { Box, LinearProgress, Tooltip, Typography, keyframes, styled } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';

const pulse = keyframes`
  0% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.7; transform: scale(1.02); }
  100% { opacity: 1; transform: scale(1); }
`;

const glow = keyframes`
  0% { box-shadow: 0 0 2px rgba(255, 0, 0, 0.2); }
  50% { box-shadow: 0 0 8px rgba(255, 0, 0, 0.6); }
  100% { box-shadow: 0 0 2px rgba(255, 0, 0, 0.2); }
`;

const StyledLinearProgress = styled(LinearProgress)<{ percentage: number }>(({ theme, percentage }) => {
  return {
    height: 6,
    borderRadius: 3,
    backgroundColor: theme.palette.grey[800],
    '& .MuiLinearProgress-bar': {
      borderRadius: 3,
      background: percentage >= 90
        ? `linear-gradient(90deg, ${theme.palette.warning.main} 0%, ${theme.palette.error.main} 100%)`
        : `linear-gradient(90deg, ${theme.palette.success.main} 0%, ${theme.palette.primary.main} 100%)`,
    },
    ...(percentage >= 90 && {
      animation: `${glow} 2s infinite ease-in-out`,
    })
  };
});

interface TokenUsageBarProps {
  spent: number;
  max: number;
  remaining: number;
}

export function TokenUsageBar({ spent, max, remaining }: TokenUsageBarProps) {
  const { t } = useTranslation();
  const safeSpent = Math.max(spent, 0);
  const safeMax = Math.max(max, 0);
  const safeRemaining = Math.max(Math.min(remaining, safeMax), 0);
  const percentage = safeMax > 0
    ? Math.min(Math.round((safeSpent / safeMax) * 100), 100)
    : 0;
  const isCritical = percentage >= 90;

  const formatTokens = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num.toString();
  };

  return (
    <Tooltip
      title={t('nav.token_usage_tooltip', {
        spent: safeSpent.toLocaleString(),
        max: safeMax.toLocaleString(),
        remaining: safeRemaining.toLocaleString(),
        percent: percentage
      })}
      arrow
      placement="bottom"
    >
      <Box sx={{
        minWidth: 120,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        cursor: 'default',
        ...(isCritical && { animation: `${pulse} 2s infinite ease-in-out` })
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 700,
              color: isCritical ? 'error.main' : 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              whiteSpace: 'nowrap',
              mr: 1
            }}
          >
            {t('nav.token_usage')}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.6rem',
              fontWeight: 600,
              color: isCritical ? 'error.main' : 'text.primary',
              whiteSpace: 'nowrap'
            }}
          >
            {t('nav.token_remaining', {
              remaining: formatTokens(safeRemaining),
            })}
          </Typography>
        </Box>
        <StyledLinearProgress
          variant="determinate"
          value={percentage}
          percentage={percentage}
        />
        <Typography
          variant="caption"
          sx={{
            fontSize: '0.58rem',
            color: 'text.secondary',
            textAlign: 'right',
            whiteSpace: 'nowrap',
          }}
        >
          {t('nav.token_used_of', {
            spent: formatTokens(safeSpent),
            max: formatTokens(safeMax),
          })}
        </Typography>
      </Box>
    </Tooltip>
  );
}
